/**
 * 워크플로를 캔버스에 여는 **한 가지 경로**.
 *
 * 탐색기의 클릭과 어시스턴트의 클라이언트 도구(open_sample_workflow · create_workflow)가 같은 것을 부른다 —
 * 여는 절차가 두 벌이면 한쪽만 고쳐지는 일이 생기므로 여기로 모았다.
 *
 * 절차: 캔버스 초기화 → 현재 워크플로 지정 → 캔버스 채우기(메모리 캐시가 있으면 그것, 없으면 저장된 버전) → 전체 맞춤.
 * 다른 워크플로로 옮겨도 저장을 강제하지 않는다 — 편집 중 캔버스는 project.workflows 캐시에 남아 돌아오면 그대로다.
 *
 * 형식 변환: 저장소(canvasSchema.migrateCanvas)가 읽으면서 이 빌드의 형태로 맞춰 주고 무엇을 바꿨는지 알려 준다.
 * **여기서 저장하지 않는다** — 캔버스 저장은 버전을 쌓는 구조라, 사용자가 다음에 저장할 때 변환 결과가 새 버전이
 * 되고 원본은 이전 버전으로 남는다. 열기만 했는데 버전이 쌓이거나 조회 전용 세션이 쓰기를 하는 일이 없다.
 */
import type { Store } from '@reduxjs/toolkit';
import { resetWorkflow, setCurrentWorkflow, setWorkflowSettings, requestFitView, setSchemaInfo } from './workflowSlice';
import { Workflow, loadWorkflowCanvas, setSavedSnapshot } from '../project/projectSlice';
import { normalizeSettings } from '../../types/workflowSettings';
import i18n from '../../i18n';
import { hydrateNodes } from '../../components/workflow/utils/definitionRefs';
import { isCanvasDirty } from '../../utils/canvasSnapshot';
import { showConfirm } from '../../components/common/AppDialog';

type AnyStore = Store<any, any> & { dispatch: any };

function feedCanvas(store: AnyStore, nodes: Workflow['nodes'], edges: Workflow['edges'], markSaved: boolean, wfSettings?: any) {
  store.dispatch(setWorkflowSettings(normalizeSettings(wfSettings)));
  // 정의를 가리키는 데이터 노드는 정의의 값을 채워 싣는다 (정의가 그 사이 바뀌었을 수 있다)
  hydrateNodes(nodes ?? []).forEach((node) => store.dispatch({ type: 'workflow/addNode', payload: node }));
  (edges ?? []).forEach((edge) => store.dispatch({ type: 'workflow/addEdge', payload: edge }));
  // 서버에서 막 읽은 캔버스는 '저장된 상태' — 리듀서가 보탠 필드까지 포함해 스토어 상태로 스냅샷을 맞춘다
  if (markSaved) {
    const w = store.getState().workflow;
    store.dispatch(setSavedSnapshot({ id: w.current!.id, nodes: w.nodes, edges: w.edges, settings: w.settings }));
  }
}

/** 새 버전을 불러올지 묻는다 — 테스트·다른 호출자가 바꿔 끼울 수 있게 인자로 받는다 */
export type ConfirmNewer = (info: { name: string; ver: number; dirty: boolean }) => Promise<boolean>;

const defaultConfirmNewer: ConfirmNewer = ({ name, ver, dirty }) => showConfirm(
  i18n.t('layout:explorer.newerVersion', { name, ver }) + (dirty ? `\n\n${i18n.t('layout:explorer.newerVersionDirty')}` : ''),
  { title: i18n.t('layout:explorer.newerVersionTitle'), okText: i18n.t('layout:explorer.newerVersionReload'), tone: dirty ? 'warning' : undefined },
);

/** 캐시가 서버의 최신 버전보다 오래됐는가 (읽은 버전을 모르면 판단하지 않는다) */
const isStale = (w: Workflow) => w.baseVerNo !== undefined && (w.curVerNo ?? 0) > w.baseVerNo;

/** 서버의 최신 버전으로 캔버스를 채운다 */
async function feedFromServer(store: AnyStore, workflow: Workflow): Promise<void> {
  const r = await store.dispatch(loadWorkflowCanvas(workflow.id));
  if (loadWorkflowCanvas.fulfilled.match(r)) {
    feedCanvas(store, r.payload.nodes, r.payload.edges, true, r.payload.settings);
    store.dispatch(setSchemaInfo(r.payload.schemaInfo ?? null));
    store.dispatch(requestFitView());
  } else {
    throw new Error((r as any).error?.message || i18n.t('common:error.canvasLoadGeneric'));
  }
}

/** @throws 저장된 캔버스를 읽지 못하면 Error */
export async function openWorkflow(store: AnyStore, workflow: Workflow, opts: { confirmNewer?: ConfirmNewer } = {}): Promise<void> {
  // 캐시가 **비어 있는데** 서버에는 저장된 버전이 있으면 캐시를 믿지 않는다.
  // 어떤 경로로든 빈 캔버스가 캐시에 남으면(읽기 실패·중간에 끊긴 열기) 그 뒤로는 눌러도 계속 비어 보이고,
  // 새로고침해야 돌아왔다 — 메모리에만 있는 상태라 사용자는 원인을 알 수 없다. 그래서 서버에서 다시 읽는다.
  const cachedEmpty = (workflow.nodes?.length ?? 0) === 0 && (workflow.edges?.length ?? 0) === 0;
  const hasSavedVersion = (workflow.curVerNo ?? 0) >= 1;
  // 단, 사용자가 일부러 비운 것(userEmptied)은 편집이다 — 그대로 보여 준다(다 지운 것이 되살아나지 않게)
  let useCache = workflow.nodes !== undefined && !(cachedEmpty && hasSavedVersion && !workflow.userEmptied);

  // 캐시를 읽은 뒤 다른 곳(속성 일괄수정·다른 사용자)이 새 버전을 저장했다 — 그대로 보여 주면 바뀐 것이 안 보인다.
  // 캔버스를 비우기 **전에** 묻는다(창이 떠 있는 동안 빈 캔버스가 보이지 않게).
  if (useCache && isStale(workflow)) {
    const snap = store.getState().project.savedSnapshots?.[workflow.id];
    const dirty = isCanvasDirty(snap, workflow.nodes, workflow.edges, workflow.settings);
    const reload = await (opts.confirmNewer ?? defaultConfirmNewer)({ name: workflow.name, ver: workflow.curVerNo ?? 0, dirty });
    if (reload) useCache = false;
  }

  store.dispatch(resetWorkflow());
  store.dispatch(setCurrentWorkflow({ id: workflow.id, name: workflow.name }));
  if (useCache) {
    // 메모리 캐시(편집 중일 수 있음) — 이미 이 빌드가 만든 형태다. 스냅샷·형식 정보는 그대로 둔다
    feedCanvas(store, workflow.nodes, workflow.edges, false, workflow.settings);
    store.dispatch(setSchemaInfo(workflow.schemaInfo ?? null));
    store.dispatch(requestFitView());   // 노드를 클릭해 확대·이동해 둔 상태여도 처음 열었을 때와 같은 화면으로
    return;
  }
  await feedFromServer(store, workflow);
}

/**
 * **지금 열려 있는** 워크플로에 새 버전이 저장됐는지 보고, 있으면 새로고침을 묻는다.
 * 속성 일괄수정 창을 닫을 때 부른다 — 그 창이 이 워크플로를 바꿔 저장했을 수 있다.
 */
export async function checkOpenWorkflowFreshness(store: AnyStore, opts: { confirmNewer?: ConfirmNewer } = {}): Promise<boolean> {
  const st = store.getState();
  const id = st.workflow.current?.id;
  const w: Workflow | undefined = id ? st.project.workflows.find((x: Workflow) => x.id === id) : undefined;
  if (!w || !isStale(w)) return false;
  const dirty = isCanvasDirty(st.project.savedSnapshots?.[w.id], st.workflow.nodes, st.workflow.edges, st.workflow.settings);
  const reload = await (opts.confirmNewer ?? defaultConfirmNewer)({ name: w.name, ver: w.curVerNo ?? 0, dirty });
  if (!reload) return false;
  store.dispatch(resetWorkflow());
  store.dispatch(setCurrentWorkflow({ id: w.id, name: w.name }));
  await feedFromServer(store, w);
  return true;
}
